import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./alerts.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../../components/Loader";
import { Helmet } from "react-helmet";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import { Home, X } from "react-feather";
import Footer from "../../../components/Footer";
import { TableLoader } from "../../../components/LoaderC";
import axios from "axios";
import AlertCard from "./components/alertCard";
import AlertFilter from "./components/alertFilter";
import dayjs from "dayjs";
import { AddLogCallAPI } from "../../../components/AddLogs";
import { toast } from 'react-smart-toaster';
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";

const ALERTS_PER_BATCH = 20;

const AlertList = ({ history, match }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [alertList, setAlertList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [filterData, setFilterData] = useState({ searchKey: "", property: "", status: "" });
    const [showdeletePopup, setShowdeletePopup] = useState(false);
    const [showClonePopup, setShowClonePopup] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [selectecId, setSelectecId] = useState(0);
    const latestRequest = useRef(0);
    const sentinelRef = useRef(null);

    // Debounce search so we don't hit the API on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => setSearchKey(filterData.searchKey), filterData.searchKey ? 400 : 0);
        return () => clearTimeout(timer);
    }, [filterData.searchKey]);

    const fetchAlerts = useCallback(async (cursor) => {
        const requestId = ++latestRequest.current;
        const isFirstBatch = !cursor;

        if (!isFirstBatch) setLoadingMore(true);

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios.get(process.env.REACT_APP_RESOURCE_API_URL + "v1/alerts/paginated", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    per_page: ALERTS_PER_BATCH,
                    cursor: cursor || undefined,
                    search: searchKey || undefined,
                    property_id: filterData.property !== "" ? filterData.property : undefined,
                    status: filterData.status !== "" ? filterData.status : undefined,
                },
            });

            // Ignore responses from requests that were superseded by newer filters
            if (requestId !== latestRequest.current) return;

            const rows = response.data?.data || [];
            const pagination = response.data?.pagination || {};

            if (isFirstBatch) {
                setAlertList(rows);
            } else {
                // A cloned alert may already be in the list, so skip duplicates
                setAlertList(prev => {
                    const loadedIds = new Set(prev.map(i => i.id));
                    return prev.concat(rows.filter(i => !loadedIds.has(i.id)));
                });
            }
            setTotalCount(pagination.total || 0);
            setHasMore(!!pagination.has_more);
            setNextCursor(pagination.next_cursor || null);
            setLoadError(false);
        } catch (error) {
            if (requestId !== latestRequest.current) return;
            console.error('[CommunityBoard] Failed to load alert list', error);
            setLoadError(true);
            setHasMore(false);
            toast.error(error?.response?.data?.message || 'Unable to load alerts. Please try again.');
        } finally {
            if (requestId === latestRequest.current) {
                setContentLoading(false);
                setLoadingMore(false);
            }
        }
    }, [getAccessTokenSilently, searchKey, filterData.property, filterData.status]);

    // Start over from the newest alert whenever the filters change
    useEffect(() => {
        fetchAlerts(null);
    }, [fetchAlerts]);

    // Load the next batch when the sentinel below the list scrolls into view
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore || loadingMore) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting)
                fetchAlerts(nextCursor);
        }, { rootMargin: "200px" });

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, nextCursor, fetchAlerts, contentLoading]);

    const confirmDelete = async () => {
        setShowdeletePopup(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.delete(process.env.REACT_APP_RESOURCE_API_URL + 'v1/alerts/' + selectecId, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);
            setLoading(false);

            setAlertList(prev => prev.filter(i => i?.id !== selectecId));
            setTotalCount(prev => Math.max(prev - 1, 0));
            setSelectecId(0);

            let logData = {
                'title': 'Comminty board has been deleted',
                'description': [
                    'Title: ' + response.data.data?.title,
                    'Valid On: ' + dayjs(response.data.data?.start_date).format('MMM D, YYYY') + ' to ' + dayjs(response.data.data?.end_date).format('MMM D, YYYY'),
                    'Properties: ' + response.data.data?.property_names.join(', ')
                ]
            }

            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const confirmCopy = async () => {
        setShowClonePopup(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.post(process.env.REACT_APP_RESOURCE_API_URL + 'v1/alerts/copy/' + selectecId, {}, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);
            setLoading(false);

            if (response.data?.data) {
                setAlertList(prev => [response.data.data].concat(prev));
                setTotalCount(prev => prev + 1);
            }
            setSelectecId(0);

            let logData = {
                'title': 'Comminty board has been cloned',
                'description': [
                    'Title: ' + response.data.data?.title,
                    'Valid On: ' + dayjs(response.data.data?.start_date).format('MMM D, YYYY') + ' to ' + dayjs(response.data.data?.end_date).format('MMM D, YYYY'),
                    'Properties: ' + response.data.data?.property_names.join(', ')
                ]
            }

            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<React.Fragment>
        {loading && <Loader />}

        <Helmet title="Community Board" />
        <div className={`${styles.pageTitleWrap}`}>
            <Typography display="inline" className={`${styles.pageTitle}`}>Community Board</Typography>
            <nav aria-label="breadcrumb">
                <ol className={`${styles.breadcrumb}`}>
                    <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Resources</li>
                    <li className={`${styles.breadcrumbItem} ${styles.active}`}>Community Board</li>
                </ol>
            </nav>
        </div>
        <div className={`${styles.BodyCon}`}>
            <div className={`${styles.mainCardDiv}`}>
                <div className={`${styles.mainCard}`}>
                    <div className="control-pane">
                        {contentLoading && (<div className="Loader TableLoader"><TableLoader /></div>)}

                        {!contentLoading && (<div className={`${styles.Row}`}>
                            <AlertFilter filterData={filterData} setFilterData={setFilterData.bind(this)} />

                            <div className={`${styles.LogMaiSec}`}>
                                {alertList.map((item, index) => {
                                    return (<AlertCard key={item?.id ?? index} item={item} setShowdeletePopup={setShowdeletePopup.bind(this)} setSelectecId={setSelectecId.bind(this)} setShowClonePopup={setShowClonePopup.bind(this)} />);
                                })}

                                {alertList.length > 0 && (<div ref={sentinelRef} className={`${styles.LoadMoreSec}`}>
                                    {loadingMore && <CircularProgress size={24} />}
                                    {!hasMore && !loadingMore && totalCount > ALERTS_PER_BATCH && (<p className={`${styles.LoadMoreText}`}>All {totalCount} alerts loaded</p>)}
                                </div>)}

                                {!contentLoading && alertList.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                                    <p className={`${styles.NoDataText}`}>Something went wrong</p>
                                    <p className={`${styles.NoDataTextSub}`}>We couldn't load alerts. Please refresh the page to try again.</p>
                                </div>)}

                                {!contentLoading && alertList.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                                    <div className={`${styles.NoDataIMG}`}>
                                        <img src="/static/img/community-alert.png" alt="No Data" />
                                    </div>
                                    <p className={`${styles.NoDataText}`}>Sorry!</p>
                                    <p className={`${styles.NoDataTextSub}`}>You have no new alert at this time.</p>
                                </div>)}

                            </div>
                        </div>)}
                    </div>
                </div>
            </div>
        </div>

        <Dialog
            open={showdeletePopup}
            onClose={(e) => setShowdeletePopup(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="alert-dialog-title">Are you sure you want to delete the alert?<button onClick={(e) => setShowdeletePopup(false)}><X /></button></DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <div className="ModalFormGroup">
                        <label className="PopupBodyText">This alert will be removed immediately. You can't undo this action.</label>
                    </div>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <button className="CancelPopupBU" onClick={(e) => confirmDelete()}>Delete</button>
                <button onClick={(e) => setShowdeletePopup(false)} className="SubmitPopupBU">Cancel</button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={showClonePopup}
            onClose={(e) => setShowClonePopup(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="alert-dialog-title">Are you sure you want to clone this alert?<button onClick={(e) => setShowClonePopup(false)}><X /></button></DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <div className="ModalFormGroup">
                        <label className="PopupBodyText">A duplicate of this alert will be created immediately. You can edit the cloned alert after it is created.</label>
                    </div>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <button onClick={(e) => setShowClonePopup(false)} className="CancelPopupBU">Cancel</button>
                <button className="SubmitPopupBU" onClick={(e) => confirmCopy()}>Copy</button>
            </DialogActions>
        </Dialog>

        <Snackbar
            open={snackbarOpen}
            onClose={(e) => setSnackbarOpen(false)}
            TransitionComponent={Slide}
            message={snackbarMsg}
            autoHideDuration={10000}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            action={
                <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={(e) => setSnackbarOpen(false)}
                >
                    <X />
                </IconButton>
            }
        />

        <Footer />
    </React.Fragment>);
};

export default withCustomAuthenticationRequired(AlertList, {
    onRedirecting: () => <Loader />,
});